import React, { useState } from 'react';
import type { MenuMultiOption, MenuOption } from '@saviynt/design-system';
import {
  Button,
  ButtonIcon,
  ButtonSelect,
  Icon,
  InputField,
  Menu,
  MenuMulti,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../../shared/hooks';

import './RoleIntelligenceControls.css';

// ── Filter definitions ───────────────────────────────────────────────────────

interface RoleFilterDef {
  id: string;
  label: string;
  /** Category shown in the add/remove menu */
  category: string;
  options: { value: string; label: string }[];
}

const ALL_FILTERS: RoleFilterDef[] = [
  {
    id: 'status',
    label: 'Status',
    category: 'Role',
    options: [
      { label: 'Compliant', value: 'Compliant' },
      { label: 'Over-licensed', value: 'Over-licensed' },
      { label: 'Dormant', value: 'Dormant' },
    ],
  },
  {
    id: 'type',
    label: 'Type',
    category: 'Role',
    options: [
      { label: 'Composite', value: 'Composite' },
      { label: 'Single', value: 'Single' },
    ],
  },
  {
    id: 'currentTier',
    label: 'Current Tier',
    category: 'Tier',
    options: [
      { label: 'Advanced', value: 'Advanced' },
      { label: 'Core', value: 'Core' },
      { label: 'Self-Service', value: 'Self-Service' },
      { label: 'Developer', value: 'Developer' },
    ],
  },
  {
    id: 'potentialTier',
    label: 'Potential Tier',
    category: 'Tier',
    options: [
      { label: 'Advanced', value: 'Advanced' },
      { label: 'Core', value: 'Core' },
      { label: 'Self-Service', value: 'Self-Service' },
      { label: 'Developer', value: 'Developer' },
    ],
  },
];

// status and type are locked — always visible, cannot be removed
const LOCKED_FILTER_IDS = ['status', 'type'];
const DEFAULT_ENABLED_FILTER_IDS = ['status', 'type'];

// ── Component ────────────────────────────────────────────────────────────────

interface RoleIntelligenceControlsProps {
  onSearch?: (query: string) => void;
  onFilterChange?: (filterId: string, values: string[]) => void;
  onExport?: () => void;
}

/**
 * @description Controls bar for the Role Intelligence tab: search input, a
 * filter toggle, filter pills (status + type by default; current/potential tier
 * addable via the add/remove menu), and an export action. Manages local search
 * and filter state, and notifies the parent on changes. Follows the same
 * add/remove pattern as the FUE Inventory controls.
 * @param {Object} props - Component props
 * @param {(query: string) => void} [props.onSearch] - Callback fired when the search input value changes
 * @param {(filterId: string, values: string[]) => void} [props.onFilterChange] - Callback fired when a filter's selected values change, with the filter id and its currently selected values
 * @param {() => void} [props.onExport] - Callback fired when the export button is clicked
 * @returns {React.ReactElement} The role intelligence controls bar
 */
function RoleIntelligenceControls({
  onSearch = undefined,
  onFilterChange = undefined,
  onExport = undefined,
}: RoleIntelligenceControlsProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [enabledFilterIds, setEnabledFilterIds] = useState<string[]>(
    DEFAULT_ENABLED_FILTER_IDS
  );
  const [filterSelections, setFilterSelections] = useState<
    Record<string, string[]>
  >({});
  const [isAddRemoveMenuOpen, setIsAddRemoveMenuOpen] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const query = e.target.value;

    setSearchQuery(query);
    onSearch?.(query);
  };

  const handleFilterChange = (filterId: string, values: string[]): void => {
    setFilterSelections((prev) => ({ ...prev, [filterId]: values }));
    onFilterChange?.(filterId, values);
  };

  const handleClearFilters = (): void => {
    setFilterSelections({});
    // Reset optional filters back to defaults, keep locked ones
    setEnabledFilterIds([...DEFAULT_ENABLED_FILTER_IDS]);
  };

  const hasActiveFilters = Object.values(filterSelections).some(
    (v) => v.length > 0
  );

  const visibleFilters = ALL_FILTERS.filter((f) =>
    enabledFilterIds.includes(f.id)
  );

  return (
    <div className='LicenseManagement-RoleIntelligenceControls'>
      <div className='LicenseManagement-RoleIntelligenceControls-topRow'>
        <div className='LicenseManagement-RoleIntelligenceControls-leftAlign'>
          <InputField
            name='role-intelligence-search'
            kind='search'
            size='medium'
            placeholder='Search by name or description'
            value={searchQuery}
            setValue={setSearchQuery}
            onChange={handleSearchChange}
            prefixIcon={
              <Icon
                kind='Search'
                size='small'
                className='LicenseManagement-RoleIntelligenceControls-searchIcon'
              />
            }
            className='LicenseManagement-RoleIntelligenceControls-searchInput'
          />

          <div className='LicenseManagement-RoleIntelligenceControls-filterButtonWrapper'>
            <Button
              className={`LicenseManagement-RoleIntelligenceControls-filterButton${
                expandedFilters
                  ? ' LicenseManagement-RoleIntelligenceControls-filterButton--active'
                  : ''
              }`}
              leftIcon={
                <Icon
                  kind='Filter'
                  className='LicenseManagement-RoleIntelligenceControls-filterIcon'
                  size='small'
                />
              }
              onClick={() => setExpandedFilters(!expandedFilters)}>
              <Typography
                className='LicenseManagement-RoleIntelligenceControls-filterLabel'
                kind='body2-bold'>
                {localize('Filter')}
              </Typography>
            </Button>
            {hasActiveFilters && (
              <span className='LicenseManagement-RoleIntelligenceControls-badge' />
            )}
          </div>
        </div>

        <div className='LicenseManagement-RoleIntelligenceControls-actions'>
          <Button
            kind='outlined'
            size='small'
            onClick={onExport}
            leftIcon={<Icon kind='Download' size='small' />}>
            {localize('Export')}
          </Button>
        </div>
      </div>

      {expandedFilters && (
        <div className='LicenseManagement-RoleIntelligenceControls-expandedFilters'>
          {visibleFilters.map((filter) => {
            const selectedValues = filterSelections[filter.id] ?? [];
            const menuOptions: MenuMultiOption[] = filter.options.map(
              (opt) => ({
                ...opt,
                isChecked: selectedValues.includes(opt.value),
              })
            );

            return (
              <div
                key={filter.id}
                className='LicenseManagement-RoleIntelligenceControls-filterPill'>
                <MenuMulti
                  label={filter.label}
                  options={menuOptions}
                  appendLabelsToButton
                  isFilteringDisabled
                  minOptionsForFooter={0}
                  trigger={<ButtonSelect kind='pill' label='' />}
                  onChange={(selected) => {
                    handleFilterChange(
                      filter.id,
                      (selected ?? [])
                        .filter((o: MenuMultiOption) => o.isChecked)
                        .map((o: MenuMultiOption) => o.value)
                    );
                  }}
                  footerLink2Text='Clear Selection'
                  onFooterLink2Click={() => handleFilterChange(filter.id, [])}
                />
              </div>
            );
          })}

          {/* Add / remove optional filters — mirrors FUE Inventory pattern */}
          <Menu
            kind='addAndRemove'
            label='Add or Remove Filters'
            className='LicenseManagement-RoleIntelligenceControls-addRemoveBtn'
            options={ALL_FILTERS.map(
              (f: RoleFilterDef): MenuOption => ({
                value: f.id,
                label: f.label,
                category: f.category,
                isChecked: enabledFilterIds.includes(f.id),
                isDisabled: LOCKED_FILTER_IDS.includes(f.id),
              })
            )}
            onChange={(opt: MenuOption) => {
              if (LOCKED_FILTER_IDS.includes(opt.value)) {
                return;
              }

              if (opt.isChecked) {
                // Remove filter and clear its selection
                handleFilterChange(opt.value, []);
                setEnabledFilterIds((prev) =>
                  prev.filter((id) => id !== opt.value)
                );
              } else {
                setEnabledFilterIds((prev) => [...prev, opt.value]);
                setIsAddRemoveMenuOpen(false);
              }
            }}
            isMenuExpanded={isAddRemoveMenuOpen}
            setIsExpanded={setIsAddRemoveMenuOpen}
            inlineMessageText=''
            isInlineMessageVisible={false}
            trigger={
              <ButtonIcon
                kind='outlinedNeutral'
                size='medium'
                ariaLabel='Add filter'
                icon={<Icon kind='Add' size='xSmall' />}
              />
            }
            shouldCloseMenuOnItemClick={false}
            placement='bottom-start'
            isPlacementLocked
          />

          {hasActiveFilters && (
            <button
              type='button'
              className='LicenseManagement-RoleIntelligenceControls-clearButton'
              onClick={handleClearFilters}>
              {localize('Clear')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

RoleIntelligenceControls.defaultProps = {
  onSearch: () => {},
  onFilterChange: () => {},
  onExport: () => {},
};

export default RoleIntelligenceControls;
